import { ManageableItemsList, SelectOption, Tooltip } from '@payaca/ambrosia';
import { FC, useEffect, useMemo, useState, type JSX } from 'react';
import { Link, useHistory } from 'react-router-dom';

import useGetMyRegionPreferences from '@/api/queries/me/useGetMyRegionPreferences';
import { useSelector } from '@/api/state';
import { usePermissions } from '@/hooks/usePermissions';
import { BulkMarkInvoicesAsPaidModal } from '@/ui/components/bulkMarkInvoicesAsPaidModal/BulkMarkInvoicesAsPaidModal';
import InvoiceStatusBadge from '@/ui/components/invoiceStatusBadge/InvoiceStatusBadge';
import { PermissionGuard } from '@/ui/components/permissionGuard/PermissionGuard';
import { useRegion } from '@/api/rest/users/useRegion';

import useGetInvoices from '@/api/queries/invoice/useGetInvoices';
import { gqlInvoiceStatusToInvoiceStatusMap } from '@/api/queries/project/useGetProjectInvoices';
import { InvoiceDueFilter, InvoicesSortBy, InvoiceStatus } from '@/gql/graphql';
import AuthenticatedPageWrapper from '@/ui/layouts/authenticatedPageWrapper/AuthenticatedPageWrapper';
import { currencyPrice } from '@payaca/helpers/financeHelper';
import {
  DateFormats,
  getInternationalDateFormatByLocale,
} from '@payaca/helpers/internationalHelper';
import { PaymentsPermissions } from '@payaca/permissions/payments/payments.permissions';
import { ReadableInvoiceStatus } from '@payaca/types/invoiceTypes';
import { SortDirection } from '@payaca/types/listViewTypes';
import UntitledIcon from '@payaca/untitled-icons';
import { format, startOfDay } from 'date-fns';
import QuickbooksLogo from '../../../assets/images/quickbooks.svg';
import XeroLogo from '../../../assets/images/xero.svg';

export const InvoicesPage: FC = (): JSX.Element | null => {
  return (
    <AuthenticatedPageWrapper>
      <div className="p-4 pb-6">
        <List />
      </div>
    </AuthenticatedPageWrapper>
  );
};

// Must not exceed the invoices query's server-side max page size (100), or the
// query silently returns fewer rows than totalCount reports.
const PAGE_SIZE = 100;

const invoiceStatusOptions: SelectOption<ReadableInvoiceStatus>[] =
  Object.values(ReadableInvoiceStatus).map((status) => ({
    label: status,
    value: status,
  }));

const dueFilterOptions: SelectOption<InvoiceDueFilter>[] = [
  { label: 'Due in future', value: 'DUE_UPCOMING' },
  { label: '0 - 30 days overdue', value: 'OVERDUE_0_30_DAYS' },
  { label: '30 - 60 days overdue', value: 'OVERDUE_30_60_DAYS' },
  { label: '60 - 90 days overdue', value: 'OVERDUE_60_90_DAYS' },
  { label: '90+ days overdue', value: 'OVERDUE_90_PLUS_DAYS' },
];

const readableStatusToGql: Record<ReadableInvoiceStatus, InvoiceStatus> = {
  [ReadableInvoiceStatus.DRAFT]: 'DRAFT',
  [ReadableInvoiceStatus.SENT]: 'SENT',
  [ReadableInvoiceStatus.PAID]: 'PAID',
  [ReadableInvoiceStatus.PARTIALLY_PAID]: 'PARTIALLY_PAID',
  [ReadableInvoiceStatus.VOID]: 'VOID',
};

// The sortable columns, keyed by their InvoiceRow field.
type SortableField = 'createdAt' | 'totalValue' | 'dueAt';

const sortFieldToGql: Record<SortableField, InvoicesSortBy> = {
  createdAt: 'CREATED_AT',
  totalValue: 'TOTAL_VALUE',
  dueAt: 'EFFECTIVE_DUE_AT',
};

type InvoiceRow = {
  id: number;
  publicId: string;
  reference: string;
  readableStatus: ReadableInvoiceStatus;
  totalValue: number;
  dueValue: number;
  createdAt: Date;
  dueAt: Date | null;
  quickbooksInvoiceId: string | null;
  xeroInvoiceId: string | null;
  projectId: number;
  projectPublicId: string;
  projectReference: string;
  projectArchived: boolean;
  customerId: number;
  customerPublicId: string;
  customerName: string;
};

const List: FC = () => {
  const region = useRegion();
  const history = useHistory();
  const { userHasRequiredPermission } = usePermissions();

  const [selectedInvoices, setSelectedInvoices] = useState<InvoiceRow[]>([]);
  const [showBulkMarkAsPaidModal, setShowBulkMarkAsPaidModal] = useState(false);

  const bulkMarkAsPaidInvoices = useMemo(
    () =>
      selectedInvoices.map((invoice) => ({
        id: invoice.id,
        dueValue: invoice.dueValue,
        reference: invoice.reference,
      })),
    [selectedInvoices]
  );

  const bulkMarkAsPaidCustomer = useMemo(() => {
    const [firstInvoice] = selectedInvoices;
    if (!firstInvoice) return null;
    return { id: firstInvoice.customerId, name: firstInvoice.customerName };
  }, [selectedInvoices]);
  const { data: accountRegionPreferences } = useGetMyRegionPreferences();

  const hasXeroConnection = useSelector((state: any) => {
    return !!state.users.myProfile.accounts[0].integrations?.xero;
  });

  const hasQuickbooksConnection = useSelector((state: any) => {
    return !!state.users.myProfile.accounts[0].integrations?.quickbooks;
  });

  const [pageNumber, setPageNumber] = useState(1);
  const [statuses, setStatuses] = useState<ReadableInvoiceStatus[]>([]);
  const [dueFilters, setDueFilters] = useState<InvoiceDueFilter[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [appliedSearchTerm, setAppliedSearchTerm] = useState('');
  // Defaults to newest-first; the Created column lets users return to this.
  const [sortBy, setSortBy] = useState<SortableField>('createdAt');
  const [sortDirection, setSortDirection] = useState<SortDirection>(
    SortDirection.DESCENDING
  );

  const { data, isLoading, refetch } = useGetInvoices({
    input: {
      searchTerm: appliedSearchTerm || undefined,
      statuses: statuses.length
        ? statuses.map((status) => readableStatusToGql[status])
        : undefined,
      dueFilters: dueFilters.length ? dueFilters : undefined,
      sortBy: sortFieldToGql[sortBy],
      sortDirection: sortDirection === SortDirection.ASCENDING ? 'ASC' : 'DESC',
    },
    pagination: { limit: PAGE_SIZE, offset: (pageNumber - 1) * PAGE_SIZE },
  });

  // Clear the selection whenever the result set changes.
  useEffect(() => {
    setSelectedInvoices([]);
  }, [
    appliedSearchTerm,
    statuses,
    dueFilters,
    sortBy,
    sortDirection,
    pageNumber,
  ]);

  const handleSort = (field: SortableField, direction: SortDirection) => {
    setSortBy(field);
    setSortDirection(direction);
    setPageNumber(1);
  };

  const invoices = useMemo<InvoiceRow[]>(
    () =>
      (data?.invoices.items ?? []).map((item) => ({
        id: Number(item.id),
        publicId: item.publicId,
        reference: `${item.reference}`,
        readableStatus: gqlInvoiceStatusToInvoiceStatusMap[item.status],
        totalValue: item.totalValue.value,
        dueValue: item.outstandingValue.value,
        createdAt: new Date(item.createdAt),
        dueAt: item.dueAt ? new Date(item.dueAt) : null,
        quickbooksInvoiceId: item.quickbooksInvoiceId ?? null,
        xeroInvoiceId: item.xeroInvoiceId ?? null,
        projectId: Number(item.project.id),
        projectPublicId: item.project.publicId,
        projectReference: item.project.reference,
        projectArchived: !!item.project.archivedAt,
        customerId: Number(item.project.customer?.id ?? 0),
        customerPublicId: item.project.customer?.publicId ?? '',
        customerName: item.project.customer?.name ?? '',
      })),
    [data]
  );

  const overdueCutoff = useMemo(() => startOfDay(new Date()), []);

  const shouldDisableItem = (invoice: InvoiceRow) => {
    if (
      [
        ReadableInvoiceStatus.DRAFT,
        ReadableInvoiceStatus.PAID,
        ReadableInvoiceStatus.VOID,
      ].includes(invoice.readableStatus)
    ) {
      return { disabled: true };
    }

    if (
      selectedInvoices.length > 0 &&
      invoice.customerId !== selectedInvoices[0].customerId
    ) {
      return { disabled: true };
    }

    return { disabled: false };
  };

  return (
    <>
      <ManageableItemsList>
        <ManageableItemsList.HeaderBar heading="Invoices" />
        <ManageableItemsList.ActionBar>
          <ManageableItemsList.ActionBar.SearchInput
            value={searchTerm}
            onChange={(value) => setSearchTerm(value)}
            onChangeTimeout={() => {
              setAppliedSearchTerm(searchTerm);
              setPageNumber(1);
            }}
            onBlur={() => {
              setAppliedSearchTerm(searchTerm);
              setPageNumber(1);
            }}
            changeTimeoutMs={300}
          />
          <ManageableItemsList.ActionBar.BasicFilter
            name="Status"
            options={invoiceStatusOptions}
            value={statuses}
            onChange={(value) => {
              setStatuses(value);
              setPageNumber(1);
            }}
          />
          <ManageableItemsList.ActionBar.BasicFilter
            name="Due"
            options={dueFilterOptions}
            value={dueFilters}
            onChange={(value) => {
              setDueFilters(value);
              setPageNumber(1);
            }}
          />
          <PermissionGuard
            renderIfHasPermissions={[
              PaymentsPermissions.BULK_MARK_INVOICES_PAID,
            ]}
          >
            <ManageableItemsList.ActionBar.GlobalAction
              onClick={() => setShowBulkMarkAsPaidModal(true)}
              tooltipContent="Select some invoices to perform this action"
              disabled={selectedInvoices.length === 0}
              enabled={selectedInvoices.length === 0}
            >
              Mark as paid
            </ManageableItemsList.ActionBar.GlobalAction>
          </PermissionGuard>
        </ManageableItemsList.ActionBar>
        <ManageableItemsList.Table
          items={invoices}
          uniqueKey="id"
          isLoading={isLoading}
          sortBy={sortBy}
          sortDirection={sortDirection}
          selectedItems={selectedInvoices}
          setSelectedItems={
            userHasRequiredPermission([
              PaymentsPermissions.BULK_MARK_INVOICES_PAID,
            ])
              ? setSelectedInvoices
              : undefined
          }
          shouldDisableItem={shouldDisableItem}
          onClickRow={(invoice) =>
            history.push(
              `/projects/${invoice.projectId}/invoices/${invoice.id}`
            )
          }
        >
          <ManageableItemsList.Table.Column
            header="Invoice"
            field="reference"
          />
          <ManageableItemsList.Table.Column
            header="Project"
            field="projectReference"
            render={(_, invoice) => (
              <div
                className="flex items-center gap-2"
                onClick={(e) => e.stopPropagation()}
              >
                <Link
                  to={`/projects/${invoice.projectPublicId}`}
                  className="max-w-24 truncate"
                >
                  {invoice.projectReference}
                </Link>
                {invoice.projectArchived && (
                  <Tooltip
                    className="flex items-center"
                    tooltipContent="This project has been archived."
                  >
                    <UntitledIcon
                      name="alert-triangle.3"
                      role="img"
                      aria-label="This project has been archived"
                      className="h-4 w-4 text-yellow-500"
                    />
                  </Tooltip>
                )}
              </div>
            )}
          />
          <ManageableItemsList.Table.Column
            header="Customer"
            field="customerName"
            render={(_, invoice) =>
              invoice.customerPublicId ? (
                <div
                  className="max-w-sm truncate"
                  onClick={(e) => e.stopPropagation()}
                >
                  <Link to={`/customers/${invoice.customerPublicId}`}>
                    {invoice.customerName}
                  </Link>
                </div>
              ) : null
            }
          />
          <ManageableItemsList.Table.Column
            header="Status"
            field="readableStatus"
            render={(_, invoice) =>
              invoice.readableStatus && (
                <InvoiceStatusBadge status={invoice.readableStatus} />
              )
            }
          />
          <ManageableItemsList.Table.Column
            header="Total"
            field="totalValue"
            onSort={(_, direction) => handleSort('totalValue', direction)}
            render={(_, invoice) => currencyPrice(invoice.totalValue, region)}
          />
          <ManageableItemsList.Table.Column
            header="Outstanding"
            field="dueValue"
            render={(_, invoice) =>
              [
                ReadableInvoiceStatus.PAID,
                ReadableInvoiceStatus.PARTIALLY_PAID,
                ReadableInvoiceStatus.SENT,
              ].includes(invoice.readableStatus)
                ? currencyPrice(invoice.dueValue, region)
                : '-'
            }
          />
          <ManageableItemsList.Table.Column
            header="Due"
            field="dueAt"
            onSort={(_, direction) => handleSort('dueAt', direction)}
            render={(_, invoice) => {
              return [
                ReadableInvoiceStatus.PARTIALLY_PAID,
                ReadableInvoiceStatus.SENT,
              ].includes(invoice.readableStatus) && invoice.dueAt ? (
                <span
                  className={
                    invoice.dueAt < overdueCutoff ? 'text-red-500' : ''
                  }
                >
                  {format(
                    invoice.dueAt,
                    getInternationalDateFormatByLocale(
                      DateFormats.SHORT,
                      accountRegionPreferences?.locale
                    )
                  )}
                </span>
              ) : (
                '-'
              );
            }}
          />
          <ManageableItemsList.Table.Column
            header="Created"
            field="createdAt"
            onSort={(_, direction) => handleSort('createdAt', direction)}
            render={(_, invoice) =>
              format(
                invoice.createdAt,
                getInternationalDateFormatByLocale(
                  DateFormats.SHORT,
                  accountRegionPreferences?.locale
                )
              )
            }
          />
          {(hasXeroConnection || hasQuickbooksConnection) && (
            <ManageableItemsList.Table.Column
              header="Synced"
              field=""
              render={(_, invoice) => {
                return (
                  <span className="flex flex-row gap-2">
                    {hasQuickbooksConnection && invoice.quickbooksInvoiceId && (
                      <a
                        href={`https://app.qbo.intuit.com/app/invoice?txnId=${invoice.quickbooksInvoiceId}`}
                        target="_blank"
                        rel="noreferrer"
                      >
                        <img
                          alt="Quickbooks"
                          src={QuickbooksLogo}
                          className="max-h-6 w-6"
                        />
                      </a>
                    )}

                    {hasXeroConnection && invoice.xeroInvoiceId && (
                      <a
                        href={`https://go.xero.com/AccountsReceivable/View.aspx?InvoiceID=${invoice.xeroInvoiceId}`}
                        target="_blank"
                        rel="noreferrer"
                      >
                        <img
                          alt="Xero"
                          src={XeroLogo}
                          className="max-h-6 w-6"
                        />
                      </a>
                    )}
                  </span>
                );
              }}
            />
          )}
        </ManageableItemsList.Table>
        {data?.invoices && (
          <ManageableItemsList.PaginationBar
            type="custom"
            totalItems={data.invoices.totalCount}
            pageSize={PAGE_SIZE}
            currentPage={pageNumber}
            onPageChange={(newPage) => setPageNumber(newPage)}
          />
        )}
      </ManageableItemsList>
      {bulkMarkAsPaidCustomer && (
        <BulkMarkInvoicesAsPaidModal
          invoices={bulkMarkAsPaidInvoices}
          customer={bulkMarkAsPaidCustomer}
          isOpen={showBulkMarkAsPaidModal}
          onClose={() => setShowBulkMarkAsPaidModal(false)}
          onSuccess={() => {
            void refetch();
          }}
        />
      )}
    </>
  );
};
